import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { SpinnerBlock } from 'Components/SpinnerBlock';

import { MoistureLogsTableContainer } from 'Containers/RocketDry';

import { getMaterialWithDryingLogs } from 'Containers/RocketDry/actions';
import { getRoomDamageMaterials } from 'Containers/RocketScan/RoomsView/DamagedMaterials/actions';

import { damagedMaterialsSelector, projectHasNewDamageTypesSelector } from 'Containers/RocketScan/selectors';

import { PurpleButton } from 'Components/Button';

import { NoRecordingsPlaceholder } from 'Components/RocketDry';

import { legacyWhitelists, dryingWhitelists } from 'Utils/rocketDryMaterialWhitelist';

import { useRocketDryFunctions } from 'Context/RocketDry';

interface Props {
  roomId: number;
}

const applyWhitelist = (material: any, isNewType: boolean) => {
  const { materialType, materialName, isCustom } = material;

  if (isCustom) {
    return true;
  }
  if (isNewType) {
    switch (materialType) {
      case 'Carpentry':
        return dryingWhitelists.carpentry.some((material) => material === materialName);
      case 'Ceiling':
        return dryingWhitelists.ceiling.some((material) => material === materialName);
      case 'Flooring':
        return dryingWhitelists.flooring.some((material) => material === materialName);
      case 'Plumbing':
        return dryingWhitelists.plumbing.some((material) => material === materialName);
      case 'Walls':
        return dryingWhitelists.walls.some((material) => material === materialName);
      default:
        return false;
    }
  } else {
    switch (materialType) {
      case 'Flooring':
        return legacyWhitelists.flooring.some((material) => material === materialName);
      case 'Walls':
        return legacyWhitelists.walls.some((material) => material === materialName);
      case 'Ceiling':
        return legacyWhitelists.ceiling.some((material) => material === materialName);
      case 'Plumbing':
        return legacyWhitelists.plumbing.some((material) => material === materialName);
      case 'Structural':
        return legacyWhitelists.structural.some((material) => material === materialName);
      default:
        return false;
    }
  }
};

function MoistureLogsContainer({ roomId }: Props) {
  const dispatch = useDispatch();

  const mounted = useRef(true);

  const {
    setSelectedMoistureLog,
    setIsViewMoistureLogModalOpen,
    setIsAddMoistureLogMaterialModalOpen,
    setSelectedRoomId,
    selectedRoomId,
    moistureLogRefetch,
    setMoistureLogRefetch,
  }: any = useRocketDryFunctions();

  const [roomMaterialList, setRoomMaterialList] = useState([]);
  const [fetching, setFetching] = useState(false);

  const allMaterialList = useSelector(damagedMaterialsSelector, areEqual);
  const useNewDamageTypes = useSelector(projectHasNewDamageTypesSelector, areEqual);

  const getRoomMaterialList = useCallback(
    async (materialList: any) => {
      setFetching(true);
      const materialsWithLogsResponse: any = await dispatch(getMaterialWithDryingLogs(roomId));
      const allRoomMaterialsResponse: any = await dispatch(getRoomDamageMaterials(roomId));

      if (mounted) {
        if (materialsWithLogsResponse?.data && allRoomMaterialsResponse?.data) {
          const { data: materialWithLogsData } = materialsWithLogsResponse;
          const { data: allRoomMaterialsData } = allRoomMaterialsResponse;
          const allRoomMaterialIds = allRoomMaterialsData.map((material) => material.id.toString());
          let damageMaterialList = [];
          damageMaterialList = Object.entries(materialWithLogsData)
            .map(([materialId, logs]) => {
              const material = materialList.find((material: any) => material.id.toString() === materialId);
              return {
                materialId,
                materialName: material?.name,
                materialType: material?.damage_type?.name,
                isCustom: !material?.is_standard,
                logs,
              };
            })
            .filter((material) => applyWhitelist(material, useNewDamageTypes))
            .filter((material) => allRoomMaterialIds.includes(material.materialId));

          setRoomMaterialList(damageMaterialList);
        } else {
          setRoomMaterialList([]);
        }

        setFetching(false);
      }
    },
    [roomId, useNewDamageTypes]
  );

  // initial fetch
  useEffect(() => {
    mounted.current = true;
    if (allMaterialList.length > 0) {
      (async function fetchData() {
        await getRoomMaterialList(allMaterialList);
      })();
    }

    return () => {
      if (mounted) {
        mounted.current = false;
      }
    };
  }, [allMaterialList]);

  // fetch on update
  useEffect(() => {
    if (moistureLogRefetch && selectedRoomId === roomId) {
      (async function fetchData() {
        await getRoomMaterialList(allMaterialList);
        setMoistureLogRefetch(false);
      })();
    }
  }, [moistureLogRefetch, roomId, selectedRoomId, allMaterialList]);

  const handleViewPicture = useCallback((log: any) => {
    setSelectedMoistureLog(log);
    setIsViewMoistureLogModalOpen(true);
  }, []);

  const handleAddMaterial = useCallback(() => {
    setIsAddMoistureLogMaterialModalOpen(true);
    setSelectedRoomId(roomId);
  }, [roomId, roomMaterialList]);

  return (
    <>
      <SpinnerBlock fetching={fetching} />
      {!fetching && roomMaterialList.length > 0 ? (
        roomMaterialList.map((material) => (
          <MoistureLogsTableContainer
            roomId={roomId}
            key={material.materialId}
            materialWithLogs={material}
            handleViewPicture={handleViewPicture}
          />
        ))
      ) : (
        <NoRecordingsPlaceholder />
      )}
      <PurpleButton onClick={handleAddMaterial}>Add new material</PurpleButton>
    </>
  );
}

const MoistureLogsContainerMemo = memo(MoistureLogsContainer, areEqual);

export { MoistureLogsContainerMemo as MoistureLogs };
